import { createContext, ReactNode, useContext, useState } from 'react';

import { PaymentAccountModal, PaymentGuideModal } from '@/components/tax-refund/summary/Modal/PaymentModal';
import { RuralSpecialTaxNoPayModal, RuralSpecialTaxPayModal } from '@/components/tax-refund/summary/Modal/RuralSpecialTaxModal';
import { VirtualAccountInfo } from '@/lib/constants/summary';

const SummaryModalContext = createContext<SummaryModalContextType | undefined>(undefined);

export const SummaryModalProvider = ({ children }: { children: ReactNode }) => {
  const [modalNameSet, setModalNameSet] = useState<string[]>([]);
  const [modalData, setModalData] = useState<Record<string, any>>({});

  const openModal = (modalName: string, additionalData?: Record<string, string>) => {
    if (!modalNameSet.includes(modalName)) {
      modalNameSet.push(modalName);
      setModalNameSet([...modalNameSet]);
      if (additionalData) {
        setModalData(additionalData);
      }
    }
  };

  const closeModal = (modalName: string) => {
    const existingModalIndex = modalNameSet.indexOf(modalName);
    if (existingModalIndex > -1) {
      modalNameSet.splice(existingModalIndex, 1);
      setModalNameSet([...modalNameSet]);
    }
  };

  return (
    <SummaryModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      <RuralSpecialTaxNoPayModal
        isOpen={modalNameSet.includes(SummaryModalName.RuralSpecialTaxNoPayModal)}
        onClose={() => closeModal(SummaryModalName.RuralSpecialTaxNoPayModal)}
      />
      <RuralSpecialTaxPayModal
        isOpen={modalNameSet.includes(SummaryModalName.RuralSpecialTaxPayModal)}
        onClose={() => closeModal(SummaryModalName.RuralSpecialTaxPayModal)}
      />
      <PaymentGuideModal
        isOpen={modalNameSet.includes(SummaryModalName.PaymentGuideModal)}
        onClose={() => closeModal(SummaryModalName.PaymentGuideModal)}
        virtualAccountInfo={modalData.virtualAccountInfo as VirtualAccountInfo}
        sectionType={modalData.sectionType as string}
      />
      <PaymentAccountModal
        isOpen={modalNameSet.includes(SummaryModalName.PaymentAccountModal)}
        onClose={() => closeModal(SummaryModalName.PaymentAccountModal)}
        virtualAccountInfo={modalData.virtualAccountInfo as VirtualAccountInfo}
        sectionType={modalData.sectionType as string}
      />
    </SummaryModalContext.Provider>
  );
};

export const useSummaryModal = () => {
  const context = useContext(SummaryModalContext);
  if (context === undefined) {
    throw new Error('useSummaryModal must be used within a SummaryModalProvider');
  }
  return context;
};

type SummaryModalContextType = {
  openModal: (modalName: string, modalData?: Record<string, any>) => void;
  closeModal: (modalName: string) => void;
};

export const SummaryModalName = {
  RuralSpecialTaxNoPayModal: 'RuralSpecialTaxNoPayModal',
  RuralSpecialTaxPayModal: 'RuralSpecialTaxPayModal',
  PaymentGuideModal: 'PaymentGuideModal',
  PaymentAccountModal: 'PaymentAccountModal'
};
